import { readMetaErrorCode } from './metaAdsConfig';
import { formatMetaMoney } from './metaAdsMoney';
import { stripMetaTerrioMark } from './metaFormat';
import { isMetaFase1Unavailable } from './metaFase1Support';
import { metaWriteStatusLabel } from './metaWrite';

/*
 * F2b · Historial «Cambios hechos desde Terrio» (`GET /getmetawritelog`, T9, §3.7). Solo el dueño.
 * Puro: lee la respuesta y arma los textos. Los montos vienen en unidad mínima (÷100), las horas en
 * UTC (se muestran en Córdoba) y el id de la cuenta se enmascara.
 */

const TZ = 'America/Argentina/Cordoba';
const str = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);
const idStr = (v) => (v == null ? null : str(String(v)));
const obj = (v) => (v && typeof v === 'object' ? v : null);
// Nombres sin la marca « [TRxxxxxxxx]» de lo creado desde Terrio.
const nameStr = (v) => str(stripMetaTerrioMark(v));

// `partial`: solo en `create` (F2b API): se creó una parte y quedó en pausa.
export const META_WRITE_LOG_RESULTS = ['ok', 'failed', 'in_progress', 'unconfirmed', 'partial'];
export const META_WRITE_LOG_PAGE = 20;
export const META_WRITE_LOG_LEVELS = [
  { key: '', label: 'Todos' },
  { key: 'campaign', label: 'Campañas' },
  { key: 'adset', label: 'Conjuntos' },
  { key: 'ad', label: 'Anuncios' },
];
const LEVEL_LABELS = { campaign: 'Campaña', adset: 'Conjunto', ad: 'Anuncio' };
export const metaWriteLogLevelLabel = (level) => LEVEL_LABELS[level] || '';

/** `****0001` (solo los últimos 4). */
export function maskMetaAdAccountId(id) {
  const v = idStr(id)?.replace(/^act_/, '');
  if (!v) return '';
  return `****${v.slice(-4)}`;
}

/** «28/10 15:40» (o con año si no es este año), hora de Córdoba. */
export function formatMetaLogTime(iso, now = new Date()) {
  const d = new Date(iso || '');
  if (Number.isNaN(d.getTime())) return '';
  const parts = (date, opts) => new Intl.DateTimeFormat('es-AR', { timeZone: TZ, ...opts }).format(date);
  const sameYear = parts(d, { year: 'numeric' }) === parts(now, { year: 'numeric' });
  const day = parts(d, sameYear ? { day: '2-digit', month: '2-digit' } : { day: '2-digit', month: '2-digit', year: 'numeric' });
  const time = parts(d, { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${day} ${time}`;
}

/** Fecha de fin («15/12/2026»). `null` → «Sin fecha». */
function formatEndDay(iso) {
  if (!iso) return 'Sin fecha';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return new Intl.DateTimeFormat('es-AR', { timeZone: TZ, day: '2-digit', month: '2-digit', year: 'numeric' }).format(d);
}

const minorMoney = (side) => {
  const minor = Number(side?.amount_minor);
  if (side?.amount_minor == null || !Number.isFinite(minor)) return '—';
  // Con centavos, 2 decimales (si no, el formato redondea al entero).
  return formatMetaMoney(minor / 100, side.currency, { fixed2: minor % 100 !== 0 });
};

const OBJECTIVE_LABELS = { traffic: 'Tráfico', outcome_traffic: 'Tráfico' };

/**
 * Creación (F2b API): «En pausa · Tráfico · ARS 3.000 por día · 10 km · Desde 18 años», con lo que se
 * pidió (`requested`; el monto en unidad mínima, ÷100). Lo que no vino no se dice.
 */
function metaWriteLogCreateChange(item) {
  const req = obj(item.requested) || {};
  const status = str(obj(item.after)?.status) || 'PAUSED';
  const objective = OBJECTIVE_LABELS[String(req.objective || '').toLowerCase()] || null;
  const minor = Number(req.daily_budget_minor);
  const budget = req.daily_budget_minor != null && Number.isFinite(minor)
    ? `${minorMoney({ amount_minor: minor, currency: req.currency })} por día`
    : null;
  const radius = Number(req.radius_km);
  const age = Number(req.age_min);
  return [
    metaWriteStatusLabel(status, 'campaign'),
    objective,
    budget,
    req.radius_km != null && Number.isFinite(radius) ? `${radius} km` : null,
    req.age_min != null && Number.isFinite(age) ? `Desde ${age} años` : null,
  ].filter(Boolean).join(' · ');
}

/**
 * «Presupuesto diario: ARS 3.000 → ARS 4.500», «Estado: Activa → En pausa», «Nombre: «A» → «B»»,
 * «Fecha de fin: 15/12/2026 → 20/12/2026». `after` null (falló, en curso, sin confirmar) → se usa
 * `requested` (lo que se pidió).
 */
export function metaWriteLogChange(item) {
  const before = obj(item.before) || {};
  const target = obj(item.after) || obj(item.requested) || {};
  switch (item.action) {
    case 'rename':
      return `Nombre: «${before.name || '—'}» → «${target.name || '—'}»`;
    case 'budget': {
      const label = item.budgetType === 'lifetime' ? 'Presupuesto total' : 'Presupuesto diario';
      return `${label}: ${minorMoney(before)} → ${minorMoney(target)}`;
    }
    case 'end_time':
      return `Fecha de fin: ${formatEndDay(before.end_time)} → ${formatEndDay(target.end_time)}`;
    case 'create':
      return metaWriteLogCreateChange(item);
    case 'pause':
    case 'activate':
    case 'archive':
    case 'delete': {
      const lvl = item.level === 'ad' || item.level === 'adset' ? 'ad' : 'campaign';
      const fallback = { pause: 'PAUSED', activate: 'ACTIVE', archive: 'ARCHIVED', delete: 'DELETED' }[item.action];
      const from = before.status ? metaWriteStatusLabel(before.status, lvl) : '—';
      return `Estado: ${from} → ${metaWriteStatusLabel(target.status || fallback, lvl)}`;
    }
    default:
      return item.metaAction || item.action || 'Cambio';
  }
}

/** Chip del resultado (`ok` no lleva chip). */
export function metaWriteLogResultView(result) {
  switch (result) {
    case 'failed': return { label: 'No se aplicó', tone: 'error', tooltip: null };
    case 'in_progress': return { label: 'En curso', tone: 'info', tooltip: null };
    case 'unconfirmed': return { label: 'Sin confirmar', tone: 'warn', tooltip: 'Revisalo en Meta' };
    case 'partial': return { label: 'Creada a medias (en pausa)', tone: 'warn', tooltip: null };
    default: return null;
  }
}

const TITLE_VERBS = {
  pause: 'Pausó', activate: 'Reactivó', rename: 'Renombró', budget: 'Cambió el presupuesto de',
  end_time: 'Cambió la fecha de fin de', archive: 'Archivó', delete: 'Eliminó',
};
const LEVEL_NOUNS = { campaign: 'la campaña', adset: 'el conjunto', ad: 'el anuncio' };

/** Título de la tarjeta, como «Actualizó Departamento 5B»: «Pausó Lotes Funes». */
export function metaWriteLogTitle(item) {
  if (item.action === 'create') return item.objectName ? `Creó «${item.objectName}»` : 'Creó una campaña';
  const verb = TITLE_VERBS[item.action] || 'Cambió';
  const name = item.objectName || LEVEL_NOUNS[item.level] || 'la campaña';
  return `${verb} ${name}`;
}

/** Texto del desplegable («Cambió el estado», «Cambió el presupuesto diario»…). */
export function metaWriteLogSummary(item) {
  switch (item.action) {
    case 'rename': return 'Cambió el nombre';
    case 'budget': return item.budgetType === 'lifetime' ? 'Cambió el presupuesto total' : 'Cambió el presupuesto diario';
    case 'end_time': return 'Cambió la fecha de fin';
    case 'create': return 'Creada desde Terrio';
    default: return 'Cambió el estado';
  }
}

/** Lo que abre el desplegable: antes → después, dónde (nivel · campaña) y la cuenta enmascarada. */
export function metaWriteLogDetails(item) {
  const where = [metaWriteLogLevelLabel(item.level), item.level !== 'campaign' ? item.campaignName : null].filter(Boolean).join(' · ');
  const account = item.adAccountName ? `${item.adAccountName} ${item.adAccountMasked}`.trim() : item.adAccountMasked;
  return [
    { key: 'change', text: item.change },
    item.objectNameCurrent ? { key: 'now', text: `Hoy se llama «${item.objectNameCurrent}»` } : null,
    where ? { key: 'where', text: where } : null,
    account ? { key: 'account', text: `Cuenta: ${account}` } : null,
  ].filter(Boolean);
}

const normalize = (v) => String(v || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

/**
 * Buscador del historial: la API no busca, filtra lo ya cargado (nombre, campaña, cambio, cuenta).
 */
export function filterMetaWriteLogItems(items, query) {
  const q = normalize(query);
  if (!q) return items;
  return items.filter((item) => normalize([
    item.title, item.summary, item.change, item.objectName, item.objectNameCurrent, item.campaignName,
    metaWriteLogLevelLabel(item.level), item.adAccountName, item.adAccountMasked, item.actorName, item.resultView?.label,
  ].filter(Boolean).join(' ')).includes(q));
}

/** Una entrada de `items[]`. */
export function readMetaWriteLogItem(raw) {
  const id = idStr(raw?.id);
  if (!id) return null;
  const level = ['campaign', 'adset', 'ad'].includes(raw.level) ? raw.level : 'campaign';
  const result = META_WRITE_LOG_RESULTS.includes(raw.result) ? raw.result : 'ok';
  const actor = obj(raw.actor);
  const actorRole = str(actor?.role) === 'editor' ? 'editor' : 'owner';
  const nameAtChange = nameStr(raw.object_name_at_change);
  const nameCurrent = nameStr(raw.object_name_current);
  const item = {
    id,
    createdAt: str(raw.created_at),
    finishedAt: str(raw.finished_at),
    // Como el historial de listas: quién (nombre de marca o «Vos») + su rol (hoy siempre el dueño).
    actorName: str(actor?.display_name) || (actor?.is_me === false ? null : 'Vos'),
    actorRole,
    adAccountMasked: maskMetaAdAccountId(raw.ad_account_id),
    adAccountName: str(raw.ad_account_name),
    level,
    objectId: idStr(raw.object_id),
    objectName: nameAtChange || nameCurrent || '',
    // Sin la marca: «A» y «A [TR…]» son el mismo nombre (no hay «Hoy se llama»).
    objectNameCurrent: raw.object_name_changed === true && nameCurrent && nameCurrent !== nameAtChange ? nameCurrent : null,
    campaignId: idStr(raw.campaign_id),
    campaignName: nameStr(raw.campaign_name),
    action: str(raw.action),
    metaAction: str(raw.meta_action),
    budgetType: str(raw.budget_type),
    before: obj(raw.before),
    after: obj(raw.after),
    requested: obj(raw.requested),
    result,
    errorCode: str(raw.error_code),
  };
  return {
    ...item,
    change: metaWriteLogChange(item),
    title: metaWriteLogTitle(item),
    summary: metaWriteLogSummary(item),
    resultView: metaWriteLogResultView(result),
  };
}

/** `data` de una página. */
export function readMetaWriteLog(res) {
  const data = res?.data && typeof res.data === 'object' ? res.data : {};
  return {
    items: (Array.isArray(data.items) ? data.items : []).map(readMetaWriteLogItem).filter(Boolean),
    nextCursor: str(data.next_cursor),
    hasMore: data.has_more === true && Boolean(str(data.next_cursor)),
    prodLocked: data.prod_locked === true,
  };
}

/**
 * Error del historial → qué se muestra. Nunca rompe el panel: sin la ruta (404 del router, red,
 * 503 sin migrar) es «Todavía no disponible», sin rojo.
 * @returns {{ kind: 'owner_only'|'unavailable'|'validation'|'error', message: string }}
 */
export function readMetaWriteLogError(res) {
  const code = readMetaErrorCode(res);
  if (code === 'META_OWNER_ONLY') return { kind: 'owner_only', message: 'Solo el dueño ve el historial.' };
  if (!res || isMetaFase1Unavailable(res) || res?._httpStatus == null) return { kind: 'unavailable', message: 'Todavía no disponible.' };
  if (code === 'VALIDATION_ERROR') return { kind: 'validation', message: 'Revisá las fechas (hasta 400 días).' };
  return { kind: 'error', message: 'No pudimos cargar el historial.' };
}

/** Períodos del historial (incluyen hoy). `all` = sin fechas. Arranca en 7 días, como el de listas. */
export const META_WRITE_LOG_PERIODS = [
  { key: 'last_7d', label: 'Últimos 7 días' },
  { key: 'last_30d', label: 'Últimos 30 días' },
  { key: 'last_90d', label: 'Últimos 90 días' },
  { key: 'all', label: 'Todo el tiempo' },
];
export const META_WRITE_LOG_DEFAULT_PERIOD = 'last_7d';
export function metaWriteLogRange(key, today) {
  const days = { last_7d: 7, last_30d: 30, last_90d: 90 }[key];
  if (!days || !/^\d{4}-\d{2}-\d{2}$/.test(today || '')) return { from: null, to: null };
  const [y, m, d] = today.split('-').map(Number);
  const from = new Date(Date.UTC(y, m - 1, d - days)).toISOString().slice(0, 10);
  return { from, to: today };
}
